import axiosInstance from "../../axiosInstance";
import type {
  PaymentCard,
  CreateCardRequest,
  UpdateCardRequest,
  CardsListResponse,
  UserBalance,
  AddBalanceRequest,
  AddBalanceResponse,
} from "./types";

/**
 * Create a new payment card
 * Tokenize and save a payment card for the authenticated customer
 */
export const createPaymentCard = async (
  data: CreateCardRequest
): Promise<PaymentCard> => {
  const { data: response } = await axiosInstance.post<PaymentCard>(
    "/user/payment/cards",
    data
  );
  return response;
};

/**
 * List all payment cards
 * Get all saved payment cards for the authenticated customer
 */
export const getPaymentCards = async (): Promise<PaymentCard[]> => {
  const { data } = await axiosInstance.get<CardsListResponse | PaymentCard[]>(
    "/user/payment/cards"
  );
  
  // Handle both array response and object with results
  if (Array.isArray(data)) {
    return data;
  }
  
  return data.results || [];
};

/**
 * Get card details
 * Retrieve details of a specific payment card
 */
export const getPaymentCardById = async (
  cardId: string
): Promise<PaymentCard> => {
  const { data } = await axiosInstance.get<PaymentCard>(
    `/user/payment/cards/${cardId}`
  );
  return data;
};

/**
 * Update card details
 * Update expiration date or replace card token
 */
export const updatePaymentCard = async (
  cardId: string,
  data: UpdateCardRequest
): Promise<PaymentCard> => {
  const { data: response } = await axiosInstance.patch<PaymentCard>(
    `/user/payment/cards/${cardId}`,
    data
  );
  return response;
};

/**
 * Delete a payment card
 * Remove a saved payment card
 */
export const deletePaymentCard = async (cardId: string): Promise<void> => {
  await axiosInstance.delete(`/user/payment/cards/${cardId}`);
};

/**
 * Get user balance
 * Retrieve the current balance of the authenticated user
 */
export const getUserBalance = async (): Promise<UserBalance> => {
  const { data } = await axiosInstance.get<UserBalance>(
    "/user/payment/balance"
  );
  return data;
};

/**
 * Add balance to user account
 * Creates a MercadoPago payment to top up the authenticated user's balance.
 */
export const addBalance = async (
  data: AddBalanceRequest
): Promise<AddBalanceResponse> => {
  const { data: response } = await axiosInstance.post<AddBalanceResponse>(
    "/user/payment/add-balance",
    data
  );
  return response;
};
